/* Free gift + progress bar manager.
   Auto-adds the gift product when the eligible cart total crosses the gift
   threshold, removes it when the cart drops back below or a discount code is
   active (the gift and codes never combine, so the customer can never end up
   paying for the "free" gift or stacking two discounts), pins the gift
   quantity to 1 and powers the variant selector on the gift line.
   Config comes from the JSON emitted by snippets/free-gift-bar.liquid. */
(() => {
  if (window.wfFreeGiftLoaded) return;
  window.wfFreeGiftLoaded = true;

  const SOURCE = 'free-gift-manager';
  const DISMISS_KEY = 'wf-free-gift-dismissed';
  let busy = false;
  let queued = false;
  let lastEligibleSeen = null;

  const config = () => {
    const el = document.querySelector('script[data-wf-gift-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  };

  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';
  const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const isGiftLine = (item) => Boolean(item.properties && item.properties._free_gift);

  /* The /discount/CODE deep links only set this cookie; the cart JSON does
     not know about the code until checkout, so both places are checked. */
  const hasCodeCookie = () =>
    document.cookie.split('; ').some((entry) => {
      const eq = entry.indexOf('=');
      return eq > 0 && entry.slice(0, eq) === 'discount_code' && entry.slice(eq + 1);
    });

  const dismissed = () => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  };
  const setDismissed = (on) => {
    try {
      if (on) sessionStorage.setItem(DISMISS_KEY, '1');
      else sessionStorage.removeItem(DISMISS_KEY);
    } catch {
      /* storage unavailable: the gift may re-add after a manual removal */
    }
  };

  const sectionsToRender = () => {
    const list = [];
    if (document.getElementById('CartDrawer')) {
      list.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      list.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    const items = document.getElementById('main-cart-items');
    if (items) list.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    const footer = document.getElementById('main-cart-footer');
    if (footer) list.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    return list;
  };

  const renderSections = (sections) => {
    if (!sections) return;
    sectionsToRender().forEach(({ id, section, selector }) => {
      const html = sections[section];
      const root = document.getElementById(id);
      if (!html || !root) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      const target = root.querySelector(selector) || root;
      if (source) target.innerHTML = source.innerHTML;
    });
  };

  const toggleEmptyState = (cart) => {
    const empty = cart.item_count === 0;
    document
      .querySelectorAll('cart-drawer, cart-drawer-items, cart-items')
      .forEach((el) => el.classList.toggle('is-empty', empty));
    const footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', empty);
  };

  const changeLine = (key, quantity, sections) =>
    fetch(`${changeUrl}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ id: key, quantity, ...(sections ? { sections } : {}) }),
    }).then((response) => response.json());

  const addGift = (variantId, sections) =>
    fetch(`${addUrl}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _free_gift: '1' } }],
        ...(sections ? { sections } : {}),
      }),
    }).then((response) => response.json());

  const finish = async (state, codeActive) => {
    if (state) {
      renderSections(state.sections);
      const cart = state.item_count !== undefined ? state : await fetch(`${cartUrl}.js`).then((r) => r.json());
      toggleEmptyState(cart);
      /* Dawn's pubsub globals are top-level consts, not window properties */
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: state });
      }
    }
    patchBars(codeActive);
  };

  /* The bar renders server-side; the only state Liquid cannot see is the
     discount_code cookie, so it is patched in here. */
  const patchBars = (codeActive) => {
    if (!codeActive) return;
    document.querySelectorAll('.wf-gcb').forEach((bar) => {
      if (bar.classList.contains('wf-gcb--code')) return;
      bar.classList.add('wf-gcb--code');
      const msg = bar.querySelector('.wf-gcb__msg');
      if (msg && bar.dataset.codeMsg) msg.textContent = bar.dataset.codeMsg;
    });
  };

  const sync = async () => {
    if (busy) {
      queued = true;
      return;
    }
    busy = true;
    try {
      const cfg = config();
      if (!cfg || !cfg.enabled) return;

      const cart = await fetch(`${cartUrl}.js`).then((r) => r.json());
      const giftLines = cart.items.filter(isGiftLine);
      /* The eligible sum comes from the server-rendered config: only Liquid
         can apply the full ruleset (bundles, compare-at sale variants and
         discounted lines do not count toward the gift). The DOM config can
         be stale when something changed the cart outside Dawn's render flow,
         so it is checked against the raw cart total and re-rendered here
         when they disagree. */
      const rawTotal = cart.items
        .filter((item) => !isGiftLine(item))
        .reduce((sum, item) => sum + item.final_line_price, 0);
      let freshCfg = config() || cfg;
      if (
        typeof freshCfg.totalCents === 'number' &&
        freshCfg.totalCents !== rawTotal
      ) {
        const sectionList = sectionsToRender().map((entry) => entry.section);
        const sections = await fetch(
          `${cartUrl}?sections=${sectionList.join(',')}`
        ).then((r) => (r.ok ? r.json() : null)).catch(() => null);
        if (sections) {
          renderSections(sections);
          freshCfg = config() || freshCfg;
        }
      }
      const eligible =
        typeof freshCfg.eligibleCents === 'number' ? freshCfg.eligibleCents : rawTotal;
      lastEligibleSeen = eligible;
      /* Codes can sit on the cart itself (cart-level for order-scoped codes,
         line-level allocations for product-scoped ones like WILD) or only in
         the /discount/ cookie - check all three. */
      const codeActive =
        hasCodeCookie() ||
        (cart.cart_level_discount_applications || []).some((app) => app.type === 'discount_code') ||
        cart.items.some((item) =>
          (item.line_level_discount_allocations || []).some(
            (alloc) => alloc.discount_application && alloc.discount_application.type === 'discount_code'
          )
        );
      const overGift = eligible >= cfg.giftCents;
      if (!overGift) setDismissed(false);

      const sections = sectionsToRender().map((entry) => entry.section);
      let state = null;

      if ((codeActive || !overGift) && giftLines.length) {
        for (let i = 0; i < giftLines.length; i += 1) {
          const last = i === giftLines.length - 1;
          state = await changeLine(giftLines[i].key, 0, last ? sections : null);
        }
      } else if (overGift && !codeActive) {
        if (giftLines.length === 0) {
          if (!dismissed()) {
            const variant = cfg.variants.find((v) => v.available);
            if (variant) state = await addGift(variant.id, sections);
          }
        } else if (giftLines.length > 1 || giftLines[0].quantity !== 1) {
          for (const extra of giftLines.slice(1)) {
            state = await changeLine(extra.key, 0, null);
          }
          state = await changeLine(giftLines[0].key, 1, sections);
        }
      }

      await finish(state, codeActive);
    } catch {
      /* network hiccup: leave the cart as it is, next update retries */
    } finally {
      busy = false;
      if (queued) {
        queued = false;
        sync();
      }
    }
  };

  const swapGiftVariant = async (key, variantId, row) => {
    if (busy || !variantId) {
      if (row) row.classList.remove('is-swapping');
      return;
    }
    busy = true;
    try {
      const sections = sectionsToRender().map((entry) => entry.section);
      await changeLine(key, 0, null);
      const state = await addGift(variantId, sections);
      await finish(state, false);
    } catch {
      /* ignored, sync() below restores a consistent state */
    } finally {
      // the section re-render replaces the row; clear the state only if the
      // old node somehow survived (e.g. a failed request)
      if (row && row.isConnected) row.classList.remove('is-swapping');
      busy = false;
      sync();
    }
  };

  /* Capture phase + stopPropagation: these change events must never reach
     Dawn's cart-items listener. For the gift select its quantity validator
     would parse the variant id as a quantity ("increments of undefined");
     for a typed 0 it would throw the minimum-of-1 error instead of
     removing the line. */
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!target || !target.closest) return;
      const select = target.closest('.wf-gift-variant');
      if (select) {
        event.stopPropagation();
        const row = select.closest('.cart-item');
        if (row) row.classList.add('is-swapping');
        swapGiftVariant(select.dataset.key, Number(select.value), row);
        return;
      }
      const qty = target.closest('.cart-drawer .quantity__input');
      if (qty && parseInt(qty.value, 10) === 0) {
        event.stopPropagation();
        const row = qty.closest('.cart-item');
        const removeControl = row && row.querySelector('cart-remove-button button, cart-remove-button a');
        if (removeControl) removeControl.click();
      }
    },
    true
  );

  /* Capture phase: the flag must be set before Dawn's remove flow fires the
     cart update, otherwise the manager instantly re-adds the gift. */
  document.addEventListener(
    'click',
    (event) => {
      if (event.target && event.target.closest && event.target.closest('.wf-gift-row cart-remove-button')) {
        setDismissed(true);
      }
    },
    true
  );

  /* The bar re-renders server-side as fresh DOM on every cart change, so a
     plain CSS transition never fires. Remember the last rendered state and
     animate each new bar node from it: fill slides from the previous width,
     checkpoints pop only when they newly complete, the message fades on
     change. */
  const BAR_STATE_KEY = 'wf-free-gift-bar-state';
  const animateBars = () => {
    document.querySelectorAll('.wf-gcb').forEach((bar) => {
      if (bar.dataset.wfAnimated) return;
      bar.dataset.wfAnimated = '1';

      let prev = null;
      try {
        prev = JSON.parse(sessionStorage.getItem(BAR_STATE_KEY));
      } catch {
        /* no previous state */
      }

      const fill = bar.querySelector('.wf-gcb__fill');
      const target = fill ? fill.style.width : '';
      const prevWidth = prev && typeof prev.w === 'string' ? prev.w : '0%';
      if (fill && target && target !== prevWidth) {
        fill.style.transition = 'none';
        fill.style.width = prevWidth;
        void fill.offsetWidth;
        fill.style.transition = '';
        requestAnimationFrame(() => {
          fill.style.width = target;
        });
      }

      const ship = bar.querySelector('.wf-gcb__stop--ship');
      const gift = bar.querySelector('.wf-gcb__stop--gift');
      const shipDone = Boolean(ship && ship.classList.contains('is-done'));
      const giftDone = Boolean(gift && gift.classList.contains('is-done'));
      if (ship && shipDone && !(prev && prev.s)) ship.classList.add('wf-pop');
      if (gift && giftDone && !(prev && prev.g)) gift.classList.add('wf-pop');

      const msg = bar.querySelector('.wf-gcb__msg');
      const msgText = msg ? msg.textContent.trim() : '';
      if (msg && prev && typeof prev.m === 'string' && prev.m !== msgText) msg.classList.add('wf-fade');

      try {
        sessionStorage.setItem(BAR_STATE_KEY, JSON.stringify({ w: target, s: shipDone, g: giftDone, m: msgText }));
      } catch {
        /* storage unavailable: bar renders without animation */
      }
    });
  };

  /* At quantity 1 Dawn's minus button only throws "minimum of 1"; render a
     trash icon there instead and let the click remove the line. */
  const TRASH_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>';

  const decorateSteppers = () => {
    document.querySelectorAll('.cart-drawer quantity-input.cart-quantity').forEach((stepper) => {
      const input = stepper.querySelector('.quantity__input');
      const minus = stepper.querySelector('button[name="minus"]');
      const wrapper = minus && minus.querySelector('.svg-wrapper');
      if (!input || !minus || !wrapper) return;
      const atOne = Number(input.value) === 1;
      const decorated = minus.classList.contains('wf-min-remove');
      if (atOne && !decorated) {
        minus.classList.add('wf-min-remove');
        minus.dataset.wfMinusHtml = wrapper.innerHTML;
        wrapper.innerHTML = TRASH_SVG;
      } else if (!atOne && decorated) {
        minus.classList.remove('wf-min-remove');
        if (minus.dataset.wfMinusHtml) wrapper.innerHTML = minus.dataset.wfMinusHtml;
      }
    });
  };

  /* Removing a line: mark the pressed trash control and its row so CSS can show
     a spinner and dim the row; the drawer re-render replaces both nodes, which
     clears the state on its own. */
  document.addEventListener(
    'click',
    (event) => {
      /* only the control the user physically pressed shows the spinner: the
         trash-minus forwards a synthetic click to the X button, which must
         not light up as well */
      if (!event.isTrusted) return;
      const t = event.target;
      if (!t || !t.closest) return;
      const control = t.closest('cart-remove-button, button.wf-min-remove');
      if (!control) return;
      control.classList.add('wf-removing');
      const row = control.closest('.cart-item');
      if (row) row.classList.add('wf-removing-row');
    },
    true
  );

  /* Capture phase: the click must not reach Dawn's quantity-input handler,
     which would stepDown and trigger the minimum-quantity error. */
  document.addEventListener(
    'click',
    (event) => {
      const minus = event.target && event.target.closest && event.target.closest('button.wf-min-remove');
      if (!minus || !minus.closest('cart-drawer')) return;
      event.preventDefault();
      event.stopPropagation();
      const row = minus.closest('.cart-item');
      const removeControl = row && row.querySelector('cart-remove-button button, cart-remove-button a');
      if (removeControl) removeControl.click();
    },
    true
  );

  const start = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || event.source !== SOURCE) sync();
      });
    }
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        animateBars();
        decorateSteppers();
        /* Dawn's add flow publishes cartUpdate BEFORE it swaps the drawer
           markup; when the freshly rendered config carries a different
           eligible sum, run the gift logic again against it. */
        const cfg = config();
        if (
          cfg &&
          typeof cfg.eligibleCents === 'number' &&
          lastEligibleSeen !== null &&
          cfg.eligibleCents !== lastEligibleSeen
        ) {
          sync();
        }
      });
    }).observe(document.body, { childList: true, subtree: true });
    animateBars();
    decorateSteppers();
    sync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
